import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Privacy Policy page component
 */
const PrivacyPolicy = () => {
  // Last updated date
  const lastUpdated = 'November 15, 2023';

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Meira</title>
        <meta name="description" content="Learn about how Meira collects, uses, and protects your personal information, and your rights regarding your data." />
      </Helmet>

      <div className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">Privacy Policy</h1>
            <p className="mt-5 max-w-xl mx-auto text-xl text-indigo-100">
              How we handle your data and protect your privacy
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-primary mx-auto">
            <p className="text-gray-500">Last Updated: {lastUpdated}</p>
            
            <p>
              At Meira, we take your privacy seriously. This Privacy Policy describes how we collect, use, 
              and disclose your personal information when you use our platform and services.
            </p>

            <h2>Information We Collect</h2>
            <p>
              We collect information from you when you register on our site, place an order, subscribe to a newsletter, 
              respond to a survey, fill out a form, or enter information on our site.
            </p>

            <h3>Personal Information</h3>
            <p>We may collect the following personal information:</p>
            <ul>
              <li>Name, email address, and contact information</li>
              <li>Login credentials (username and password)</li>
              <li>Profile information (such as profile pictures, job title, and educational background)</li>
              <li>Billing information (for paid services)</li>
              <li>Educational records (for students and teachers)</li>
              <li>Communication preferences</li>
            </ul>

            <h3>Usage Data</h3>
            <p>
              We also collect information about how you use our platform, including:
            </p>
            <ul>
              <li>Log data (IP address, browser type, referring/exit pages, operating system, date/time stamps)</li>
              <li>Device information (hardware model, operating system version, unique device identifiers)</li>
              <li>Usage patterns and preferences</li>
              <li>Pages viewed</li>
              <li>Time spent on pages</li>
              <li>Interaction with features</li>
              <li>Error logs</li>
            </ul>

            <h2>How We Use Your Information</h2>
            <p>We use the information we collect for various purposes, including:</p>
            <ul>
              <li>Providing, operating, and maintaining our services</li>
              <li>Creating and managing user accounts</li>
              <li>Processing payments and transactions</li>
              <li>Sending administrative information (such as account updates or policy changes)</li>
              <li>Personalizing your experience and delivering content tailored to your interests</li>
              <li>Improving our platform and services</li>
              <li>Responding to your inquiries and providing customer support</li>
              <li>Communicating about new features, promotions, or events (with your consent)</li>
              <li>Monitoring usage patterns and conducting data analysis to enhance user experience</li>
              <li>Preventing fraudulent activity and ensuring the security of our platform</li>
              <li>Complying with legal obligations</li>
            </ul>

            <h2>Information Sharing and Disclosure</h2>
            <p>We may share your information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors and service providers who help us deliver our services (e.g., payment processors, cloud storage providers, email service providers)</li>
              <li><strong>Educational Institutions:</strong> If you're using Meira through your school or educational institution, we may share information with that institution as necessary</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, regulation, legal process, or governmental request</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of all or a portion of our assets</li>
              <li><strong>With Your Consent:</strong> We may share information with third parties when you have given us your consent to do so</li>
            </ul>
            <p>
              We do not sell your personal information to third parties for marketing purposes.
            </p>

            <h2>Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our platform and hold certain information. 
              Cookies are files with a small amount of data that may include an anonymous unique identifier.
            </p>
            <p>
              We use cookies for:
            </p>
            <ul>
              <li>Keeping you signed in</li>
              <li>Understanding how you use our platform</li>
              <li>Remembering your preferences</li>
              <li>Improving user experience</li>
              <li>Analytics purposes</li>
            </ul>
            <p>
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. 
              However, if you do not accept cookies, you may not be able to use some portions of our platform.
            </p>

            <h2>Data Security</h2>
            <p>
              We take the security of your data seriously and implement appropriate technical and organizational measures to protect your personal information. 
              However, no method of transmission over the Internet or method of electronic storage is 100% secure, 
              and we cannot guarantee absolute security.
            </p>
            <p>
              We have procedures in place to deal with any suspected personal data breach and will notify you and any applicable regulator of a breach where we are legally required to do so.
            </p>

            <h2>Data Retention</h2>
            <p>
              We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. 
              We will retain and use your information to the extent necessary to comply with our legal obligations, 
              resolve disputes, and enforce our policies.
            </p>

            <h2>Your Rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul>
              <li><strong>Access:</strong> You can request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> You can request that we correct inaccurate or incomplete information about you</li>
              <li><strong>Deletion:</strong> You can request that we delete your personal information in certain circumstances</li>
              <li><strong>Restriction:</strong> You can request that we restrict the processing of your information in certain circumstances</li>
              <li><strong>Data Portability:</strong> You can request a copy of your personal information in a structured, commonly used, and machine-readable format</li>
              <li><strong>Objection:</strong> You can object to our processing of your personal information in certain circumstances</li>
              <li><strong>Withdrawal of Consent:</strong> Where we rely on your consent to process your personal information, you have the right to withdraw your consent at any time</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the contact information provided below.
            </p>

            <h2>Children's Privacy</h2>
            <p>
              Our services are intended for use by individuals who are at least 13 years old. If you are a parent or guardian and believe that your child has provided us with personal information without your consent, please contact us. If we become aware that we have collected personal information from children without verification of parental consent, we will take steps to remove that information from our servers.
            </p>
            <p>
              For school-based accounts where students under 13 may use our services, we adhere to the Children's Online Privacy Protection Act (COPPA) and only collect information as permitted by schools acting as the parent's agent.
            </p>

            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this page. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>

            <h2>Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <ul>
              <li>By email: privacy@meiracorp.com</li>
              <li>By mail: Meira, 123 Education Lane, San Francisco, CA 94107</li>
              <li>By phone: +1 (555) 123-4567</li>
            </ul>

            <h2>Legal Compliance</h2>
            <p>
              This Privacy Policy has been designed to comply with applicable privacy laws, including the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other relevant regulations in the jurisdictions where we operate.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
